import { useState, useEffect } from 'react'
import { v4 as uuidv4 } from 'uuid'
import { Alert, Button, Conditional, kuiperClassNames } from '@opallabs/kuiper'
import { PositionedArrayManager } from '@opallabs/lambda'
import { AnalyticsLayer } from '@opallabs/quanta'
import DraggableInput from './DraggableInput'
import placementDragImage from './DraggableInput/dragImage'
import { useDraggableList } from './useDraggableList'
import './styles.scss'

const deleteTitle = 'Are you sure want to delete this?'
export const deleteMessage =
  'Deleting this placement will remove all traces of it through out this workspace. ' +
  'All content assigned to this placement will also be un-assigned.'

interface Placement {
  id: string
  position: number
  name: string
  isNew?: boolean
}

type UpdateAttributes = {
  accountId: string
  name: string
  placementId: string
  position: number
}

type CreateAttributes = {
  accountId: string
  name: string
  id: string
  position: number
}

type PlacementCreatorProps = {
  accountId: string
  createPlacement: (arg: CreateAttributes) => Promise<Record<string, any>>
  destroyPlacement: (arg: {
    accountId: string
    placementId: string
  }) => Promise<Record<string, any>>
  getPlacements: (arg: { accountId: string }) => Promise<Record<string, any>>
  placements: Array<Placement> | []
  updatePlacement: (arg: UpdateAttributes) => Promise<Record<string, any>>
}

export const PlacementCreator: React.FC<PlacementCreatorProps> = ({
  accountId,
  createPlacement,
  destroyPlacement,
  getPlacements,
  placements,
  updatePlacement,
}) => {
  const [newPlacements, setNewPlacements] = useState<Array<Placement> | []>([])
  const [showAlert, setShowAlert] = useState(false)
  const [placementToDelete, setPlacementToDelete] = useState<string>()
  const allPlacements: Array<Placement> | [] = [
    ...placements,
    ...newPlacements,
  ].sort((a, b) => a.position - b.position)

  useEffect(() => {
    getPlacements({ accountId }).catch(() => new Error())
  }, [accountId, getPlacements])

  const updatePosition = (
    positionArray: Array<number | null>,
    placement: Placement,
    resetDragState?: () => void
  ) => {
    const newPosition = positionArray.find(
      (position) => position !== null
    ) as number

    if (placement.isNew === true) {
      const newPlacementsToAlter = newPlacements
      const currentIndex = newPlacements.findIndex((p) => p.id === placement.id)
      newPlacementsToAlter.splice(currentIndex, 1, {
        ...placement,
        position: newPosition,
      })
      setNewPlacements(newPlacementsToAlter)
    } else {
      updatePlacement({
        accountId,
        name: placement.name,
        placementId: placement.id,
        position: newPosition,
      })
        .then(() => resetDragState && resetDragState())
        .catch((err) => {
          throw err
        })
    }
  }

  const {
    dragging,
    draggedFrom,
    draggedItemId,
    dragZone,
    onDragComplete,
    onDragEnter,
    onDragOver,
    onDragStart,
  } = useDraggableList<Placement>({
    dragImageSource: placementDragImage,
    list: allPlacements,
    updatePosition,
  })

  const onAddPlacement = () => {
    const positionedArrayManager = new PositionedArrayManager()
    const positionArray = positionedArrayManager.insertAt(
      allPlacements,
      allPlacements.length
    )
    if (!positionArray.includes(null)) {
      allPlacements.forEach((placement) => {
        updatePosition(positionArray, placement)
      })
    }

    const newPlacementPosition = positionArray[allPlacements.length] as number
    const newPlacement = {
      id: uuidv4(),
      name: '',
      position: newPlacementPosition,
      isNew: true,
    }
    setNewPlacements([...newPlacements, newPlacement])
  }

  const updatePlacementName = (name: string, id: string) => {
    const placement = allPlacements.find((p) => p.id === id) as Placement
    if (!name || placement.name === name) return null

    if (placement.isNew) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { isNew, ...placementAttributes } = placement
      const index = newPlacements.findIndex((p) => p.id === placement.id)
      const newPlacementsToAlter = newPlacements

      newPlacementsToAlter.splice(index, 1)
      setNewPlacements(newPlacementsToAlter)

      return createPlacement({ ...placementAttributes, accountId, name }).catch(
        (err) => {
          throw err
        }
      )
    }

    updatePlacement({
      accountId,
      name,
      placementId: placement.id,
      position: placement.position,
    }).catch((err) => {
      throw err
    })
  }

  const onDeleteInput = (placementId: string) => {
    setPlacementToDelete(placementId)
    setShowAlert(true)
  }

  const onDeletePlacement = () => {
    const index = newPlacements.findIndex((p) => p.id === placementToDelete)

    if (index !== -1) {
      const newPlacementsToAlter = newPlacements
      newPlacementsToAlter.splice(index, 1)
      setNewPlacements(newPlacementsToAlter)
      return setShowAlert(false)
    }

    destroyPlacement({ accountId, placementId: placementToDelete as string })
      .then(() => setShowAlert(false))
      .catch((err) => {
        throw err
      })
  }

  return (
    <AnalyticsLayer name="Placements Admin">
      <div className={kuiperClassNames('placement-creator')}>
        <ol className={kuiperClassNames('placement-list')}>
          {allPlacements.map((placement, i) => (
            <DraggableInput
              id={placement.id}
              key={placement.id}
              deleteInput={onDeleteInput}
              draggedFrom={draggedFrom}
              dragging={dragging}
              dragZone={i}
              isDragged={draggedItemId === placement.id}
              isNew={placement.isNew}
              onDragEnd={onDragComplete}
              dragOver={dragZone === i}
              onAddPlacement={onAddPlacement}
              onDrop={onDragComplete}
              onDragEnter={onDragEnter}
              onDragOver={onDragOver}
              onDragStart={onDragStart}
              updateInputValue={updatePlacementName}
              inputValue={placement.name}
            />
          ))}
        </ol>
        <Button
          className="add-placement-button"
          type="nobutton"
          typeAttribute="button"
          icon="plus-solid"
          label="Add item"
          onClick={onAddPlacement}
        />
        <Conditional condition={showAlert}>
          <div className={kuiperClassNames('delete-alert-wrapper')}>
            <Button
              className="cancel"
              label="Cancel"
              type="nobutton"
              icon="close-sm"
              size="medium"
              onClick={() => setShowAlert(false)}
            />
            <Alert message={deleteMessage} title={deleteTitle}>
              <Button
                className="cancel-button"
                label="Cancel"
                type="nobutton"
                onClick={() => setShowAlert(false)}
              />
              <Button
                className="delete-button"
                color="red"
                label="Delete Placement"
                onClick={onDeletePlacement}
              />
            </Alert>
          </div>
        </Conditional>
      </div>
    </AnalyticsLayer>
  )
}
